import React, { type FC } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

interface SignInFormProps {
  content: typeof import('./translations.json')['en'];
  email: string;
  error: string | null;
  loading: boolean;
  onEmailChange: (email: string) => void;
  onSubmit: () => Promise<void>;
}

const SignInForm: FC<SignInFormProps> = ({
  content,
  email,
  error,
  loading,
  onEmailChange,
  onSubmit,
}) => {
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onSubmit();
  };

  return (
    <>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
        {content.title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4 }}>
        {content.description}
      </Typography>

      {error && (
        <Typography variant="body2" sx={{ color: '#ef4444', mb: 2, fontWeight: 500 }}>
          {error}
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#ffffff' }}>
          {content.email}
        </Typography>
        <TextField
          fullWidth
          id="email"
          placeholder={content.emailPlaceholder}
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          disabled={loading}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '8px',
              color: '#ffffff',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
              '&:hover fieldset': { borderColor: '#1976d2' },
              '&.Mui-focused fieldset': { borderColor: '#1976d2' },
            },
            '& .MuiInputBase-input::placeholder': { color: '#64748b', opacity: 1 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ pl: 0.5 }}>
                <i className="fa-regular fa-envelope" style={{ color: '#1976d2' }}></i>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          size="large"
          disabled={loading || !email.trim()}
          sx={{
            mb: 4,
            fontSize: '15px',
            fontWeight: 600,
            py: 1.5,
            borderRadius: '8px',
            backgroundColor: '#1976d2',
            color: '#ffffff',
            '&:hover': { backgroundColor: '#115293' },
            '&.Mui-disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <>
              {content.signIn} &nbsp; <i className="fa-solid fa-arrow-right" style={{ fontSize: '14px' }}></i>
            </>
          )}
        </Button>
      </Box>
    </>
  );
};

export default SignInForm;
